import type {
  CapturedSlicerSelection,
  CapturedVisual,
  ReportFilterCaptureResult,
  ReportVisualCaptureResult
} from '../../../lib/app/powerbi/report-visual-capture';
import type {
  AskReport,
  AskReportFilterValue,
  AskReportFilters,
  AskReportVisualManifest
} from '../types/syntiro-agent';

/** The PowerBI visual id behind each handle the manifest advertises. Kept client-side so a later
 * request for `v3` can be re-exported from the live report; the ids themselves are never sent. */
export type VisualIdsByHandle = Map<string, string>;

function toManifestVisual(visual: CapturedVisual, handle: string): AskReportVisualManifest {
  return {
    handle,
    title: visual.title || 'Untitled visual',
    type: visual.type,
    columns: visual.columns
  };
}

function toFilterValue(selection: CapturedSlicerSelection): AskReportFilterValue {
  return {
    target: selection.target,
    operator: selection.operator,
    values: selection.values
  };
}

export function buildAskReportFilters(filterCapture: ReportFilterCaptureResult): AskReportFilters {
  return {
    pageName: filterCapture.pageName || 'Untitled page',
    slicerFilters: filterCapture.slicerSelections.map(toFilterValue)
  };
}

/**
 * Turns a full capture into the report `POST /ask` takes, plus the handle-to-visual-id map that
 * lets a later tool call re-export what it asks for. The exported rows are used only to derive the
 * manifest's columns and are then discarded — they would be out of date by the time the model
 * asked for them, and this capture ran cheaply enough (see `MANIFEST_CAPTURE_ROW_LIMIT`) that they
 * are not a true row count either.
 */
export function buildCapturedAskReport(
  reportCaptureResult: ReportVisualCaptureResult,
  capturedAtMs: number
): { report: AskReport; visualIdsByHandle: VisualIdsByHandle } | null {
  if (reportCaptureResult.status !== 'ok' || !reportCaptureResult.visuals) {
    return null;
  }

  const visualIdsByHandle: VisualIdsByHandle = new Map();
  const visuals: AskReportVisualManifest[] = [];

  reportCaptureResult.visuals.forEach((visual, index) => {
    const handle = `v${index + 1}`;
    visualIdsByHandle.set(handle, visual.id);
    visuals.push(toManifestVisual(visual, handle));
  });

  const pageName = reportCaptureResult.pageName || 'Untitled page';
  const pageDisplayName = reportCaptureResult.pageDisplayName || pageName;

  return {
    report: {
      reportManifest: {
        capturedAtMs,
        pageDisplayName,
        visuals,
        ...(reportCaptureResult.failedVisualTitles?.length
          ? { failedVisualTitles: reportCaptureResult.failedVisualTitles }
          : {})
      },
      reportFilters: {
        pageName,
        slicerFilters: (reportCaptureResult.slicerSelections ?? []).map(toFilterValue)
      }
    },
    visualIdsByHandle
  };
}
